import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { Clock, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getMyShiftSummaries } from '../../services/api';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const formatMXN = (amount) => {
  if (amount == null) return '\u2014';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const CierresTurno = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getMyShiftSummaries(params);
      setData(res.data);
    } catch { toast.error('Error cargando cierres de turno'); }
    finally { setLoading(false); }
  }, [page, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Clock className="w-7 h-7 text-teal-600" />
          Cierres de Turno
        </h1>
        <p className="text-slate-500 mt-1">Resúmenes de cierres de turno sincronizados desde tu POS</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Desde</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Hasta</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline">Limpiar</button>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Sucursal</th>
                <th className="text-left px-4 py-3">Caja</th>
                <th className="text-left px-4 py-3">Cajero</th>
                <th className="text-left px-4 py-3">Apertura</th>
                <th className="text-left px-4 py-3">Cierre</th>
                <th className="text-right px-4 py-3">Ventas</th>
                <th className="text-right px-4 py-3">Total Ventas</th>
                <th className="text-right px-4 py-3">Diferencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">No hay cierres de turno</td></tr>
              )}
              {data.items.map(item => {
                const diff = item.cash_difference ?? (item.expected_cash != null && item.actual_cash != null
                  ? item.actual_cash - item.expected_cash : null);
                return (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">{item.branch_name || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-600">{item.register_name || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-600">{item.cashier_name || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(item.opened_at)}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(item.closed_at)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{item.total_transactions ?? '\u2014'}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-700">{formatMXN(item.total_sales)}</td>
                    <td className="px-4 py-3 text-right">
                      {diff != null ? (
                        <span className={diff >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {diff >= 0 ? '+' : ''}{formatMXN(diff)}
                        </span>
                      ) : '\u2014'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination page={page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </DashboardLayout>
  );
};

export default CierresTurno;
